import { useEffect } from 'react';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Traps keyboard focus within a container element while a modal is open.
 * Also handles Escape key to close the modal.
 *
 * @param {React.RefObject} containerRef - ref attached to the modal container
 * @param {object} options
 * @param {function} options.onEscape  - called when Escape is pressed
 * @param {boolean}  options.enabled   - pass false to disable (default true)
 */
export function useFocusTrap(containerRef, { onEscape, enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    // Remember the element that had focus before the modal opened
    const previouslyFocused = document.activeElement;

    // Move focus into the modal on open
    const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS));
    if (focusables.length) focusables[0].focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onEscape?.();
        return;
      }

      if (e.key !== 'Tab') return;

      const current = Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS));
      if (!current.length) return;

      const first = current[0];
      const last  = current[current.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the element that was focused before the modal opened
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [containerRef, onEscape, enabled]);
}
